import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Loader2, MapPin, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['Boys', 'Girls', 'Mixed'];

export const CreateLeague = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    age_category: '',
    gender: '',
    start_date: '',
    end_date: '',
    entry_fee: '',
    max_teams: '16',
    weeks: '8',
    games_per_week: '1',
    location: '',
    venue_name: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || (user.role !== 'admin' && user.role !== 'organizer')) {
      toast.error('Only organizers can create leagues');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        entry_fee: parseFloat(formData.entry_fee),
        max_teams: parseInt(formData.max_teams),
        weeks: parseInt(formData.weeks),
        games_per_week: parseInt(formData.games_per_week)
      };
      
      const response = await axios.post(`${API}/leagues`, payload);
      toast.success('League created successfully!');
      navigate(`/leagues/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create league');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Trophy className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-2xl font-black">CREATE LEAGUE</CardTitle>
              <CardDescription>Set up a new seasonal league</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* League Name */}
            <div className="space-y-2">
              <Label htmlFor="name">League Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Spring 2025 League"
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your league..."
                rows={3}
                required
              />
            </div>

            {/* Age & Gender Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Age Category *</Label>
                <Select 
                  value={formData.age_category} 
                  onValueChange={(v) => setFormData({ ...formData, age_category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select age group" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Gender *</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(v) => setFormData({ ...formData, gender: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date *</Label>
                <Input
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Entry Fee */}
            <div className="space-y-2">
              <Label htmlFor="entry_fee">Entry Fee ($) *</Label>
              <Input
                id="entry_fee"
                name="entry_fee"
                type="number"
                min="0"
                step="0.01"
                value={formData.entry_fee}
                onChange={handleChange}
                placeholder="100.00"
                required
              />
              <p className="text-xs text-muted-foreground">
                + $10.00 team registration fee will be added at checkout
              </p>
            </div>

            {/* League Configuration */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="weeks">Weeks *</Label>
                <Input
                  id="weeks"
                  name="weeks"
                  type="number"
                  min="1"
                  value={formData.weeks}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="games_per_week">Games/Week *</Label>
                <Input
                  id="games_per_week"
                  name="games_per_week"
                  type="number"
                  min="1"
                  value={formData.games_per_week}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_teams">Max Teams *</Label>
                <Select 
                  value={formData.max_teams} 
                  onValueChange={(v) => setFormData({ ...formData, max_teams: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[4, 8, 12, 16, 20, 24].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} Teams</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Location */}
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                Location Details
              </h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">City, State *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Dallas, TX"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="venue_name">Venue Name</Label>
                  <Input
                    id="venue_name"
                    name="venue_name"
                    value={formData.venue_name}
                    onChange={handleChange}
                    placeholder="City Sports Complex"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading || !formData.age_category || !formData.gender || !formData.location}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create League'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
